import type { SpecGroup, TranslatableString } from '@geoic/shared';

export type ParsedSpec = {
  groups: SpecGroup[];
  /** Nechta qator o'qildi — xodimga «12 ta qator qo'shildi» deb aytish uchun. */
  rowCount: number;
  /** O'qib bo'lmagan qatorlar (bo'sh kalit bilan). */
  skipped: number;
};

const TAB = '\t';

/**
 * Excel/Google Sheets dan yopishtirilgan matnni xarakteristikalarga o'giradi
 * (TZ 7.3 — B8 DoD).
 *
 * Format: qatorlar `\n` bilan, ustunlar `\t` bilan ajratiladi — buferga
 * jadval aynan shunday tushadi.
 *
 *   Kamera                          ← bitta ustun → GURUH sarlavhasi
 *   Sensor      1/1.3" CMOS         ← ikki ustun  → kalit + qiymat
 *   Diafragma   f/1.7
 *
 * Nima uchun bitta ustunli qator guruh deb qabul qilinadi: xodim Excel'da
 * xuddi shunday yozadi — bo'lim nomi alohida qatorda, ustidagi kataklar
 * bo'sh. Buni qo'lda ajratishga majburlash yopishtirishning ma'nosini
 * yo'qotardi.
 *
 * Qiymatlar FAQAT o'zbek tiliga yoziladi: buferdagi jadvalda til
 * ma'lumoti yo'q va uni taxmin qilish noto'g'ri tarjima yaratardi.
 * Qolgan tillarni xodim o'zi to'ldiradi (yoki `uz` ga zaxira ishlaydi).
 */
export function parseSpecPaste(text: string, defaultGroupTitle = 'Xarakteristikalar'): ParsedSpec {
  const groups: SpecGroup[] = [];
  let current: SpecGroup | null = null;
  let rowCount = 0;
  let skipped = 0;

  // `\r\n` va yolg'iz `\r` — Windows va eski Mac'dan kelgan matn uchun
  for (const rawLine of text.split(/\r\n|\r|\n/)) {
    const cells = rawLine.split(TAB).map(unquote);
    const filled = cells.filter((cell) => cell !== '');

    if (filled.length === 0) continue;

    // Bitta to'ldirilgan ustun — guruh sarlavhasi
    if (filled.length === 1) {
      current = { title: uz(filled[0] ?? ''), rows: [], order: groups.length };
      groups.push(current);
      continue;
    }

    const key = cells.find((cell) => cell !== '') ?? '';
    const keyIndex = cells.indexOf(key);
    // Kalitdan KEYINGI barcha ustunlar qiymat: uch ustunli jadvalda
    // oxirgisi tashlab yuborilsa, ma'lumot jimgina yo'qolardi.
    const value = cells
      .slice(keyIndex + 1)
      .filter((cell) => cell !== '')
      .join(' ');

    if (key === '') {
      skipped += 1;
      continue;
    }

    if (!current) {
      current = { title: uz(defaultGroupTitle), rows: [], order: 0 };
      groups.push(current);
    }

    current.rows.push({ key: uz(key), value: uz(value) });
    rowCount += 1;
  }

  // Bo'sh guruhlar tashlanadi: matn oxiridagi yolg'iz sarlavha qatori
  // adminda bo'sh guruh yaratib qo'yardi.
  const nonEmpty = groups
    .filter((group) => group.rows.length > 0)
    .map((group, index) => ({ ...group, order: index }));

  return { groups: nonEmpty, rowCount, skipped };
}

/**
 * Excel qiymatni qo'shtirnoqqa oladi, agar ichida vergul yoki yangi qator
 * bo'lsa. Ular olib tashlanmasa, kataloglarda `"1/1.3"" CMOS"` ko'rinishida
 * chiqardi.
 */
function unquote(cell: string): string {
  const trimmed = cell.trim();
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/""/g, '"').trim();
  }
  return trimmed;
}

function uz(value: string): TranslatableString {
  return { uz: value, ru: '', en: '' };
}
